// src/utils/constants.js
// ✅ Allineato al backend: piani, valute, ruoli, metodi pagamento Africa

// ==================== API ====================
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';
export const API_TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT) || 30000;

// ==================== PIANI ====================
export const PLANS = {
    FREE: 'FREE',
    STARTER: 'STARTER',
    PRO: 'PRO',
    ENTERPRISE: 'ENTERPRISE',
};

export const PLAN_LABELS = {
    [PLANS.FREE]: 'Free',
    [PLANS.STARTER]: 'Starter',
    [PLANS.PRO]: 'Pro',
    [PLANS.ENTERPRISE]: 'Enterprise',
};

export const PLAN_COLORS = {
    [PLANS.FREE]: 'gray',
    [PLANS.STARTER]: 'blue',
    [PLANS.PRO]: 'purple',
    [PLANS.ENTERPRISE]: 'gold',
};

// Limiti frontend (fallback — quelli autoritativi vengono dal backend via /subscriptions/current)
export const PLAN_LIMITS = {
    [PLANS.FREE]: {
        maxProjects: 3, maxUsers: 5, maxExpenses: 100,
        maxStorageMb: 500, maxApiCalls: 1000,
        aiEnabled: false, aiRequestsPerMonth: 0,
        reportsEnabled: true, apiAccess: false,
        prioritySupport: false, exportPdf: true, multiCurrency: false,
    },
    [PLANS.STARTER]: {
        maxProjects: 10, maxUsers: 10, maxExpenses: 500,
        maxStorageMb: 2048, maxApiCalls: 5000,
        aiEnabled: true, aiRequestsPerMonth: 50,
        reportsEnabled: true, apiAccess: false,
        prioritySupport: false, exportPdf: true, multiCurrency: true,
    },
    [PLANS.PRO]: {
        maxProjects: -1, maxUsers: -1, maxExpenses: -1,
        maxStorageMb: 10240, maxApiCalls: 50000,
        aiEnabled: true, aiRequestsPerMonth: 200,
        reportsEnabled: true, apiAccess: true,
        prioritySupport: true, exportPdf: true, multiCurrency: true,
    },
    [PLANS.ENTERPRISE]: {
        maxProjects: -1, maxUsers: -1, maxExpenses: -1,
        maxStorageMb: 102400, maxApiCalls: -1,
        aiEnabled: true, aiRequestsPerMonth: -1,
        reportsEnabled: true, apiAccess: true,
        prioritySupport: true, exportPdf: true, multiCurrency: true,
    },
};

// ==================== RUOLI ====================
export const ROLES = {
    SUPER_ADMIN: 'SUPER_ADMIN',
    SITE_MANAGER: 'SITE_MANAGER',
    ENGINEER: 'ENGINEER',
    ACCOUNTANT: 'ACCOUNTANT',
    FIELD_SUPERVISOR: 'FIELD_SUPERVISOR',
    VIEWER: 'VIEWER',
};

export const ROLE_LABELS = {
    [ROLES.SUPER_ADMIN]: 'Super Admin',
    [ROLES.SITE_MANAGER]: 'Capo Cantiere',
    [ROLES.ENGINEER]: 'Ingegnere',
    [ROLES.ACCOUNTANT]: 'Contabile',
    [ROLES.FIELD_SUPERVISOR]: 'Supervisore',
    [ROLES.VIEWER]: 'Visualizzatore',
};

export const ROLE_COLORS = {
    [ROLES.SUPER_ADMIN]: 'red',
    [ROLES.SITE_MANAGER]: 'blue',
    [ROLES.ENGINEER]: 'green',
    [ROLES.ACCOUNTANT]: 'purple',
    [ROLES.FIELD_SUPERVISOR]: 'orange',
    [ROLES.VIEWER]: 'gray',
};

// Gerarchia permessi (più alto = più permessi)
export const ROLE_HIERARCHY = {
    [ROLES.SUPER_ADMIN]: 6,
    [ROLES.SITE_MANAGER]: 5,
    [ROLES.ENGINEER]: 4,
    [ROLES.ACCOUNTANT]: 3,
    [ROLES.FIELD_SUPERVISOR]: 2,
    [ROLES.VIEWER]: 1,
};

export const hasPermission = (userRole, requiredRole) =>
    (ROLE_HIERARCHY[userRole] || 0) >= (ROLE_HIERARCHY[requiredRole] || 0);

// ==================== STATI PROGETTO ====================
export const PROJECT_STATUS = {
    DRAFT: 'DRAFT',
    PLANNED: 'PLANNED',
    IN_PROGRESS: 'IN_PROGRESS',
    ON_HOLD: 'ON_HOLD',
    DELAYED: 'DELAYED',
    COMPLETED: 'COMPLETED',
    CANCELLED: 'CANCELLED',
};

export const PROJECT_STATUS_LABELS = {
    [PROJECT_STATUS.DRAFT]: 'Bozza',
    [PROJECT_STATUS.PLANNED]: 'Pianificato',
    [PROJECT_STATUS.IN_PROGRESS]: 'In Corso',
    [PROJECT_STATUS.ON_HOLD]: 'In Attesa',
    [PROJECT_STATUS.DELAYED]: 'In Ritardo',
    [PROJECT_STATUS.COMPLETED]: 'Completato',
    [PROJECT_STATUS.CANCELLED]: 'Cancellato',
};

export const PROJECT_STATUS_COLORS = {
    [PROJECT_STATUS.DRAFT]: 'gray',
    [PROJECT_STATUS.PLANNED]: 'blue',
    [PROJECT_STATUS.IN_PROGRESS]: 'green',
    [PROJECT_STATUS.ON_HOLD]: 'yellow',
    [PROJECT_STATUS.DELAYED]: 'orange',
    [PROJECT_STATUS.COMPLETED]: 'purple',
    [PROJECT_STATUS.CANCELLED]: 'red',
};

// ==================== PRIORITÀ ====================
export const PRIORITY = {
    LOW: 'LOW',
    MEDIUM: 'MEDIUM',
    HIGH: 'HIGH',
    URGENT: 'URGENT',
    CRITICAL: 'CRITICAL',
};

export const PRIORITY_LABELS = {
    [PRIORITY.LOW]: 'Bassa',
    [PRIORITY.MEDIUM]: 'Media',
    [PRIORITY.HIGH]: 'Alta',
    [PRIORITY.URGENT]: 'Urgente',
    [PRIORITY.CRITICAL]: 'Critica',
};

export const PRIORITY_COLORS = {
    [PRIORITY.LOW]: 'gray',
    [PRIORITY.MEDIUM]: 'blue',
    [PRIORITY.HIGH]: 'orange',
    [PRIORITY.URGENT]: 'red',
    [PRIORITY.CRITICAL]: 'purple',
};

// ==================== CATEGORIE SPESE ====================
export const EXPENSE_CATEGORIES = {
    MATERIALS: 'MATERIALS',
    EQUIPMENT: 'EQUIPMENT',
    LABOR: 'LABOR',
    FUEL: 'FUEL',
    SUBCONTRACT: 'SUBCONTRACT',
    TRANSPORT: 'TRANSPORT',
    PERMITS: 'PERMITS',
    ADMIN: 'ADMIN',
    OTHER: 'OTHER',
};

export const EXPENSE_CATEGORY_LABELS = {
    [EXPENSE_CATEGORIES.MATERIALS]: 'Materiali',
    [EXPENSE_CATEGORIES.EQUIPMENT]: 'Attrezzature',
    [EXPENSE_CATEGORIES.LABOR]: 'Manodopera',
    [EXPENSE_CATEGORIES.FUEL]: 'Carburante',
    [EXPENSE_CATEGORIES.SUBCONTRACT]: 'Subappalti',
    [EXPENSE_CATEGORIES.TRANSPORT]: 'Trasporti',
    [EXPENSE_CATEGORIES.PERMITS]: 'Permessi',
    [EXPENSE_CATEGORIES.ADMIN]: 'Amministrative',
    [EXPENSE_CATEGORIES.OTHER]: 'Altro',
};

export const EXPENSE_CATEGORY_ICONS = {
    [EXPENSE_CATEGORIES.MATERIALS]: '🧱',
    [EXPENSE_CATEGORIES.EQUIPMENT]: '🚜',
    [EXPENSE_CATEGORIES.LABOR]: '👷',
    [EXPENSE_CATEGORIES.FUEL]: '⛽',
    [EXPENSE_CATEGORIES.SUBCONTRACT]: '🤝',
    [EXPENSE_CATEGORIES.TRANSPORT]: '🚛',
    [EXPENSE_CATEGORIES.PERMITS]: '📋',
    [EXPENSE_CATEGORIES.ADMIN]: '📁',
    [EXPENSE_CATEGORIES.OTHER]: '📦',
};

// ==================== METODI PAGAMENTO ====================
export const PAYMENT_METHODS = {
    CASH: 'CASH',
    BANK_TRANSFER: 'BANK_TRANSFER',
    CHECK: 'CHECK',
    CARD: 'CARD',
    WAVE: 'WAVE',
    ORANGE_MONEY: 'ORANGE_MONEY',
    MTN_MONEY: 'MTN_MONEY',
};

export const PAYMENT_METHOD_LABELS = {
    [PAYMENT_METHODS.CASH]: '💵 Contanti',
    [PAYMENT_METHODS.BANK_TRANSFER]: '🏦 Bonifico',
    [PAYMENT_METHODS.CHECK]: '📝 Assegno',
    [PAYMENT_METHODS.CARD]: '💳 Carta',
    [PAYMENT_METHODS.WAVE]: '🌊 Wave',
    [PAYMENT_METHODS.ORANGE_MONEY]: '🟠 Orange Money',
    [PAYMENT_METHODS.MTN_MONEY]: '🟡 MTN Money',
};

// ==================== VALUTE ====================
export const CURRENCIES = {
    EUR: 'EUR',
    GBP: 'GBP',
    GNF: 'GNF',
    XOF: 'XOF',
};

export const SUPPORTED_CURRENCIES = [
    { code: 'EUR', label: 'Euro', symbol: '€', region: 'europe' },
    { code: 'GBP', label: 'Sterlina', symbol: '£', region: 'europe' },
    { code: 'GNF', label: 'Franco Guineano', symbol: 'FG', region: 'africa' },
    { code: 'XOF', label: 'Franco CFA', symbol: 'CFA', region: 'africa' },
];

export const CURRENCY_SYMBOLS = {
    EUR: '€', GBP: '£', GNF: 'FG', XOF: 'CFA',
};

// ==================== LINGUE ====================
export const SUPPORTED_LANGUAGES = [
    { code: 'it', label: 'Italiano', flag: '🇮🇹' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
];

// ==================== NOTIFICHE ====================
export const NOTIFICATION_TYPES = {
    INFO: 'info',
    SUCCESS: 'success',
    WARNING: 'warning',
    ERROR: 'error',
};

export const NOTIFICATION_TYPE_LABELS = {
    [NOTIFICATION_TYPES.INFO]: 'Info',
    [NOTIFICATION_TYPES.SUCCESS]: 'Successo',
    [NOTIFICATION_TYPES.WARNING]: 'Attenzione',
    [NOTIFICATION_TYPES.ERROR]: 'Errore',
};

// ==================== STORAGE KEYS ====================
export const STORAGE_KEYS = {
    TOKEN: 'roadctrl_token',
    REFRESH_TOKEN: 'roadctrl_refresh',
    USER: 'roadctrl_user',
    THEME: 'roadctrl_theme',
    LANGUAGE: 'roadctrl_lang',
};

// ==================== ERROR MESSAGES ====================
export const ERROR_MESSAGES = {
    NETWORK_ERROR: 'Errore di connessione. Verifica la tua connessione internet.',
    SERVER_ERROR: 'Errore del server. Riprova più tardi.',
    UNAUTHORIZED: 'Non autorizzato. Effettua il login.',
    FORBIDDEN: 'Accesso negato. Non hai i permessi necessari.',
    NOT_FOUND: 'Risorsa non trovata.',
    VALIDATION_ERROR: 'Errore di validazione. Controlla i dati inseriti.',
    LIMIT_REACHED: 'Limite raggiunto. Passa a un piano superiore.',
    AI_UPGRADE: 'Funzionalità AI non disponibile nel piano attuale.',
    AI_QUOTA: 'Limite AI mensile raggiunto. Riprova il mese prossimo.',
};

// ==================== AI ====================
export const AI_FEATURES = {
    CHAT: 'chat',
    SUGGESTIONS: 'suggestions',
    ANALYZE_COSTS: 'analyze_costs',
};

// ==================== ROUTES ====================
export const ROUTES = {
    HOME: '/',
    LOGIN: '/login',
    REGISTER: '/register',
    DASHBOARD: '/dashboard',
    PROJECTS: '/projects',
    EXPENSES: '/expenses',
    REPORTS: '/reports',
    AI_CHAT: '/ai-chat',
    SETTINGS: '/settings',
    PROFILE: '/profile',
};

// ==================== TASK STATUS ====================
export const TASK_STATUS = {
    TODO: 'TODO',
    IN_PROGRESS: 'IN_PROGRESS',
    BLOCKED: 'BLOCKED',
    COMPLETED: 'COMPLETED',
};

export const TASK_STATUS_LABELS = {
    [TASK_STATUS.TODO]: 'Da fare',
    [TASK_STATUS.IN_PROGRESS]: 'In corso',
    [TASK_STATUS.BLOCKED]: 'Bloccato',
    [TASK_STATUS.COMPLETED]: 'Completato',
};

// ==================== REGIONI ====================
export const REGIONS = {
    EUROPE: 'EUROPE',
    WEST_AFRICA: 'WEST_AFRICA',
};

export const REGION_LABELS = {
    [REGIONS.EUROPE]: 'Europa',
    [REGIONS.WEST_AFRICA]: 'Africa Occidentale',
};

// Metodi pagamento disponibili per regione
export const REGION_PAYMENT_METHODS = {
    [REGIONS.EUROPE]: [PAYMENT_METHODS.CASH, PAYMENT_METHODS.BANK_TRANSFER, PAYMENT_METHODS.CHECK, PAYMENT_METHODS.CARD],
    [REGIONS.WEST_AFRICA]: [PAYMENT_METHODS.CASH, PAYMENT_METHODS.WAVE, PAYMENT_METHODS.ORANGE_MONEY, PAYMENT_METHODS.MTN_MONEY],
};